import React, { useRef, useState } from 'react';
import ModalButton from '../UI/ModalButton';
import LanguageSwitcher from '../UI/LanguageSwitcher';
import { useLanguage } from '../../context/LanguageContext';
import { PaymentApiService } from '../../services/paymentApi';

interface PendingDialogProps {
  paymentId: string;
  timeRemaining: number;
  onRefresh: () => void;
  onBackToMerchant?: () => void;
  refreshing?: boolean;
  supportsReceipt?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

const PendingDialog: React.FC<PendingDialogProps> = ({
  paymentId,
  timeRemaining,
  onRefresh,
  onBackToMerchant,
  refreshing = false,
  supportsReceipt = false
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}${t('minutesShort')}:${secs.toString().padStart(2, '0')}${t('secondsShort')}`;
  };

  const isDanger = timeRemaining <= 60;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadState('error');
      setUploadError(t('receiptInvalidFormat'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadState('error');
      setUploadError(t('receiptFileTooLarge'));
      return;
    }

    setUploadState('uploading');
    setUploadError(null);

    try {
      await PaymentApiService.uploadReceipt(paymentId, file);
      setUploadState('success');
    } catch (error) {
      setUploadState('error');
      if (error instanceof Error) {
        if (error.message === 'File too large') {
          setUploadError(t('receiptFileTooLarge'));
        } else if (error.message === 'Please wait before uploading again') {
          setUploadError(t('receiptRateLimited'));
        } else if (error.message === 'Invalid file format') {
          setUploadError(t('receiptInvalidFormat'));
        } else {
          setUploadError(t('receiptUploadError'));
        }
      } else {
        setUploadError(t('receiptUploadError'));
      }
    }
  };

  const ClockIcon = () => (
    <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="80" cy="80" r="76" fill="#EFF6FF" stroke="#DBEAFE" strokeWidth="8"/>
      <circle cx="80" cy="80" r="56" fill="white" stroke="#3B82F6" strokeWidth="4"/>
      <path d="M80 48V80L100 100" stroke="#3B82F6" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="80" cy="80" r="6" fill="#3B82F6"/>
    </svg>
  );

  const UploadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17 8L12 3L7 8" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 3V15" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 6L9 17L4 12" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const getUploadButtonContent = () => {
    switch (uploadState) {
      case 'uploading':
        return (
          <>
            <span className="pending-dialog__upload-spinner" />
            <span>{t('receiptUploading')}</span>
          </>
        );
      case 'success':
        return (
          <>
            <CheckIcon />
            <span>{t('receiptUploaded')}</span>
          </>
        );
      default:
        return (
          <>
            <UploadIcon />
            <span>{t('uploadReceipt')}</span>
          </>
        );
    }
  };

  return (
    <div className="pending-dialog">
      <div className="dialog-header">
        <LanguageSwitcher />
      </div>
      <div className="pending-dialog__content-wrapper">
        <div className="pending-dialog__icon">
          <ClockIcon />
        </div>

        <div className="pending-dialog__content">
          <h2 className="pending-dialog__title">{t('pendingTitle')}</h2>
          <p className="pending-dialog__desc">{t('pendingDescription')}</p>
          <p className="pending-dialog__keep-open">{t('pendingKeepOpen')}</p>
        </div>

        {supportsReceipt && (
          <div className={`pending-dialog__upload ${uploadState === 'success' ? 'pending-dialog__upload--success' : ''} ${uploadState === 'error' ? 'pending-dialog__upload--error' : ''}`}>
            <p className="pending-dialog__upload-hint">{t('receiptUploadHint')}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className={`pending-dialog__upload-btn ${uploadState === 'success' ? 'pending-dialog__upload-btn--success' : ''}`}
              onClick={handleUploadClick}
              disabled={uploadState === 'uploading' || uploadState === 'success'}
            >
              {getUploadButtonContent()}
            </button>
            {uploadState === 'error' && uploadError && (
              <p className="pending-dialog__upload-error">{uploadError}</p>
            )}
            {uploadState !== 'error' && (
              <p className="pending-dialog__upload-formats">{t('receiptFormats')}</p>
            )}
          </div>
        )}

        <div className="payment-form__status">
          <div className="payment-form__status-left">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#111827" strokeWidth="2"/>
              <path d="M12 6V12L16 14" stroke="#111827" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{t('waitingForTransfer')}</span>
          </div>
          <div className={`payment-form__timer ${isDanger ? 'payment-form__timer--danger' : ''}`}>
            {formatTime(timeRemaining)}
          </div>
        </div>
      </div>

      <div className="pending-dialog__actions">
        <ModalButton
          variant="primary"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? t('processing') : t('refreshStatus')}
        </ModalButton>

        {onBackToMerchant && (
          <ModalButton
            variant="outline"
            onClick={onBackToMerchant}
          >
            {t('backToMerchant')}
          </ModalButton>
        )}
      </div>
    </div>
  );
};

export default PendingDialog;
